/**
 * Order money the portal can show honestly (kanban c81b5b33).
 *
 * `orders.subtotal` is the sum of `line_total` over the order's lines, and
 * `line_total` is NULL until a line has a unit price — so the subtotal
 * silently leaves unpriced lines out (Calco PO 26-6293: a $1,298.55
 * subtotal that covered one of two lines). Nothing here changes pricing;
 * these helpers only count what the subtotal skipped and say which of
 * freight, surcharge and tax are already known so the modal can show a
 * total, or label the amount as a subtotal before those.
 */

/** Item code of the explicit surcharge line (mirrors
 *  services/orders/surcharge_line.py::SURCHARGE_ITEM_CODE). Kept here rather
 *  than in the service module so tests that mock the service keep it. */
export const SURCHARGE_ITEM_CODE = '/SURCHG';

// ─── Unpriced lines ─────────────────────────────────────────────────────────

/** Lines whose `unit_price` is unset: the subtotal does not include them. */
export function countUnpricedLines(
  lines: ReadonlyArray<{ unit_price: string | number | null | undefined }>,
): number {
  let n = 0;
  for (const line of lines) {
    if (line.unit_price == null || line.unit_price === '') n += 1;
  }
  return n;
}

/** "1 line unpriced" / "3 lines unpriced"; null when every line has a price. */
export function unpricedLinesLabel(count: number | null | undefined): string | null {
  if (count == null || count <= 0) return null;
  return `${count} ${count === 1 ? 'line' : 'lines'} unpriced`;
}

// ─── Total ──────────────────────────────────────────────────────────────────

export type OrderTotalComponent = 'freight' | 'surcharge' | 'tax';

/** A money amount plus where it came from, for the hint under the total. */
export interface KnownAmount {
  amount: number;
  source:
    | 'invoice'
    | 'sage_open_order'
    | 'truckload_preset'
    | 'order_freight_cost'
    | 'surcharge_line';
}

/** What the modal knows about the charges that sit on top of the subtotal. */
export interface OrderTotalInputs {
  subtotal: string | number | null | undefined;
  /** Freight the portal knows for sure; null when nothing authoritative is on file. */
  freight: KnownAmount | null;
  /** The explicit /SURCHG line amount — already inside the subtotal. Null = no line. */
  surchargeLine: KnownAmount | null;
  /** Sales tax from the invoice; null until invoiced. */
  tax: KnownAmount | null;
  /** The invoice's own total once invoiced: the final word, used as-is. */
  invoiceTotal: string | number | null | undefined;
}

export interface OrderTotalView {
  /** 'total' when at least one component beyond the subtotal is known. */
  kind: 'total' | 'subtotal';
  amount: number;
  /** Components included in `amount`, in display order. */
  included: Array<{ component: OrderTotalComponent; amount: number; source: KnownAmount['source'] }>;
  /** Components not yet known, so still to come on the invoice. */
  pending: OrderTotalComponent[];
  /** True when `amount` is the invoice's total_amt rather than a sum. */
  fromInvoice: boolean;
}

function toNumber(val: string | number | null | undefined): number | null {
  if (val == null || val === '') return null;
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return Number.isFinite(n) ? n : null;
}

/** Round to cents so a sum of decimal strings does not show $1,298.5500000001. */
function cents(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Decide what amount to show and what to call it.
 *
 * - Invoiced: the invoice total is final; freight and tax come from it and
 *   nothing is pending.
 * - Otherwise: subtotal + every known component. The surcharge line is
 *   never added (it is already a line inside the subtotal) but it does count
 *   as known. If nothing beyond the subtotal is known the view is a
 *   `subtotal` with all three components pending, so the UI labels it
 *   "before freight, surcharge and tax" instead of calling it a total.
 */
export function resolveOrderTotal(inputs: OrderTotalInputs): OrderTotalView {
  const subtotal = toNumber(inputs.subtotal) ?? 0;
  const invoiceTotal = toNumber(inputs.invoiceTotal);

  if (invoiceTotal != null) {
    const included: OrderTotalView['included'] = [];
    if (inputs.freight) included.push({ component: 'freight', amount: inputs.freight.amount, source: inputs.freight.source });
    if (inputs.surchargeLine) included.push({ component: 'surcharge', amount: inputs.surchargeLine.amount, source: inputs.surchargeLine.source });
    if (inputs.tax) included.push({ component: 'tax', amount: inputs.tax.amount, source: inputs.tax.source });
    return { kind: 'total', amount: cents(invoiceTotal), included, pending: [], fromInvoice: true };
  }

  const included: OrderTotalView['included'] = [];
  const pending: OrderTotalComponent[] = [];
  let amount = subtotal;

  if (inputs.freight) {
    included.push({ component: 'freight', amount: inputs.freight.amount, source: inputs.freight.source });
    amount += inputs.freight.amount;
  } else {
    pending.push('freight');
  }

  if (inputs.surchargeLine) {
    // Inside the subtotal already — report it, do not add it twice.
    included.push({ component: 'surcharge', amount: inputs.surchargeLine.amount, source: inputs.surchargeLine.source });
  } else {
    pending.push('surcharge');
  }

  if (inputs.tax) {
    included.push({ component: 'tax', amount: inputs.tax.amount, source: inputs.tax.source });
    amount += inputs.tax.amount;
  } else {
    pending.push('tax');
  }

  return {
    kind: included.length > 0 ? 'total' : 'subtotal',
    amount: cents(amount),
    included,
    pending,
    fromInvoice: false,
  };
}

/** "freight, surcharge and tax" / "surcharge and tax" / "tax". */
export function joinComponents(components: ReadonlyArray<OrderTotalComponent>): string {
  if (components.length === 0) return '';
  if (components.length === 1) return components[0];
  return `${components.slice(0, -1).join(', ')} and ${components[components.length - 1]}`;
}

// ─── Reading the detail payload ─────────────────────────────────────────────

/** The slice of PortalOrderDetailResponse the total needs. Structural so tests stay small. */
export interface OrderTotalDetailSlice {
  order: { subtotal: string | null; freight_cost: string | null };
  line_items: ReadonlyArray<{ item_code: string; unit_price: string | null; line_total: string | null }>;
  invoice?: {
    freight_amt: string | number | null;
    sales_tax_amt: string | number | null;
    total_amt: string | number | null;
  } | null;
  sage_freight?: { freight_amt: string | number | null } | null;
  truckload_freight?: { total: string | number } | null;
  freight_error?: unknown | null;
}

/**
 * The freight the portal can vouch for, in the same precedence the modal's
 * Freight row uses: invoice → open-SO mirror → truckload preset → the
 * order's own freight_cost. A $0 from Sage while a freight-error notice is
 * open means "not keyed yet", not "free", so it is treated as unknown.
 */
export function resolveKnownFreight(detail: OrderTotalDetailSlice): KnownAmount | null {
  const invoiceAmt = toNumber(detail.invoice?.freight_amt);
  if (invoiceAmt != null && !(detail.freight_error && invoiceAmt === 0)) {
    return { amount: invoiceAmt, source: 'invoice' };
  }
  const sageAmt = toNumber(detail.sage_freight?.freight_amt);
  if (sageAmt != null && !(detail.freight_error && sageAmt === 0)) {
    return { amount: sageAmt, source: 'sage_open_order' };
  }
  const preset = toNumber(detail.truckload_freight?.total);
  if (preset != null) return { amount: preset, source: 'truckload_preset' };
  const own = toNumber(detail.order.freight_cost);
  if (own != null) return { amount: own, source: 'order_freight_cost' };
  return null;
}

/** The explicit /SURCHG line's amount, if the order carries one. */
export function resolveSurchargeLine(detail: OrderTotalDetailSlice): KnownAmount | null {
  const line = detail.line_items.find((li) => li.item_code === SURCHARGE_ITEM_CODE);
  if (!line) return null;
  const amount = toNumber(line.line_total) ?? toNumber(line.unit_price);
  if (amount == null) return null;
  return { amount, source: 'surcharge_line' };
}

/** Sales tax is only ever known from the invoice. */
export function resolveKnownTax(detail: OrderTotalDetailSlice): KnownAmount | null {
  const amt = toNumber(detail.invoice?.sales_tax_amt);
  return amt == null ? null : { amount: amt, source: 'invoice' };
}

/** One call for the modal: everything above, from the detail payload. */
export function orderTotalFromDetail(detail: OrderTotalDetailSlice): OrderTotalView {
  return resolveOrderTotal({
    subtotal: detail.order.subtotal,
    freight: resolveKnownFreight(detail),
    surchargeLine: resolveSurchargeLine(detail),
    tax: resolveKnownTax(detail),
    invoiceTotal: detail.invoice?.total_amt,
  });
}
